/**
 * ChangelogUnsubscribePage — public landing for the one-click
 * unsubscribe link in every digest email.
 *
 * Hits GET /api/changelog/unsubscribe/{token} (always 200, doesn't
 * leak whether the token existed) and shows a friendly confirmation.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Sparkle, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function ChangelogUnsubscribePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await api.get(`/changelog/unsubscribe/${encodeURIComponent(token || "")}`); }
      finally { if (alive) setDone(true); }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900 flex items-center justify-center p-4"
         data-testid="changelog-unsubscribe-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md shadow-sm p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-blue-600" />
          <h1 className="text-base font-bold tracking-tight">What's New</h1>
        </div>
        {!done && <p className="text-sm text-slate-500" data-testid="unsub-busy">Removing you from the list…</p>}
        {done && (
          <div data-testid="unsub-success" className="space-y-2">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle size={18} weight="fill" />
              <p className="text-sm font-bold">You're unsubscribed.</p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              We won't email you again. You can still view release notes any time on the What's New page.
            </p>
            <button
              data-testid="unsub-go-whatsnew"
              onClick={() => nav("/whats-new")}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800">
              View What's New <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
